import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput, CFormCheck,
  CButton, CRow, CCol, CAlert, CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilUser, cilLockLocked, cilEnvelopeClosed, cilSettings, cilSave, cilArrowLeft } from '@coreui/icons';
import api from '../services/api';

const EMPTY = { username: '', password: '', nama: '', email: '', isadmin: false, isaccount: false, isactive: true, google_email: null };

function SectionCard({ icon, title, children, className = 'mb-3' }) {
  return (
    <CCard className={className}>
      <CCardHeader>
        <CIcon icon={icon} className="me-2 text-success" />{title}
      </CCardHeader>
      <CCardBody>{children}</CCardBody>
    </CCard>
  );
}

function FieldLabel({ icon, children }) {
  return (
    <CFormLabel className="d-flex align-items-center gap-2 mb-1">
      <CIcon icon={icon} size="sm" className="text-success" />{children}
    </CFormLabel>
  );
}

export default function AdminUserFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/users/${id}`)
      .then(r => {
        const u = r.data.user;
        setForm({
          username: u.username || '', password: '',
          nama: u.nama || '', email: u.email || '',
          isadmin: !!u.isadmin, isaccount: !!u.isaccount, isactive: !!u.isactive,
          google_email: u.google_email || null,
        });
      })
      .catch(() => setError('Failed to load user.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      if (!payload.email) delete payload.email;
      if (isEdit) {
        await api.put(`/admin/users/${id}`, payload);
        setSuccess('User updated.');
      } else {
        await api.post('/admin/users', payload);
        setSuccess('User created.');
        setForm(EMPTY);
      }
    } catch (err) {
      const msgs = err.response?.data?.errors?.map(e => e.msg).join(', ');
      setError(msgs || err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-5"><CSpinner color="success" /></div>;

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">{isEdit ? 'Edit User' : 'Add User'}</h5></CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" onClick={() => navigate('/admin/users')}>
            <CIcon icon={cilArrowLeft} className="me-1" /> Back
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {success && <CAlert color="success">{success}</CAlert>}

      <CForm onSubmit={handleSubmit}>
        <SectionCard icon={cilUser} title="Account">
          <CRow>
            <CCol md={6}>
              <FieldLabel icon={cilUser}>Username <span className="text-danger">*</span></FieldLabel>
              <CFormInput name="username" value={form.username} onChange={handleChange} required maxLength={30} disabled={isEdit} placeholder="e.g. ahmad" />
            </CCol>
            <CCol md={6}>
              <FieldLabel icon={cilLockLocked}>
                {isEdit ? 'New Password (leave blank to keep)' : <>Password <span className="text-danger">*</span></>}
              </FieldLabel>
              <CFormInput
                type="password" name="password" value={form.password} onChange={handleChange}
                required={!isEdit} minLength={6} maxLength={60}
                placeholder={isEdit ? 'Leave blank to keep current' : 'Min 6 characters'}
              />
            </CCol>
          </CRow>
        </SectionCard>

        <SectionCard icon={cilUser} title="Personal Info">
          <CRow>
            <CCol md={6}>
              <FieldLabel icon={cilUser}>Full Name <span className="text-danger">*</span></FieldLabel>
              <CFormInput name="nama" value={form.nama} onChange={handleChange} required maxLength={55} placeholder="e.g. Ahmad bin Ali" />
            </CCol>
            <CCol md={6}>
              <FieldLabel icon={cilEnvelopeClosed}>Email</FieldLabel>
              <CFormInput type="email" name="email" value={form.email} onChange={handleChange} maxLength={35} placeholder="e.g. ahmad@email.com" />
            </CCol>
          </CRow>
        </SectionCard>

        {isEdit && (
          <SectionCard icon={cilSettings} title="Google Sign-In" className="mb-3">
            {form.google_email ? (
              <div className="d-flex align-items-center gap-3">
                <svg viewBox="0 0 24 24" width="24" height="24" style={{ flexShrink: 0 }}>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <div className="fw-semibold small" style={{ color: '#1a73e8' }}>Connected</div>
                  <div className="text-muted small">{form.google_email}</div>
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-3 text-muted">
                <svg viewBox="0 0 24 24" width="24" height="24" style={{ flexShrink: 0, opacity: 0.25 }}>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#aaa"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#aaa"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#aaa"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#aaa"/>
                </svg>
                <span className="small">No Google account linked. User can link from My Profile.</span>
              </div>
            )}
          </SectionCard>
        )}

        <SectionCard icon={cilSettings} title="Permissions" className="mb-4">
          <div className="d-flex gap-4">
            <CFormCheck name="isadmin" label="Admin" checked={form.isadmin} onChange={handleChange} />
            <CFormCheck name="isaccount" label="Account/Finance" checked={form.isaccount} onChange={handleChange} />
            <CFormCheck name="isactive" label="Active" checked={form.isactive} onChange={handleChange} />
          </div>
        </SectionCard>

        <div className="d-flex gap-2 mb-4">
          <CButton type="submit" color="success" disabled={saving}>
            <CIcon icon={cilSave} className="me-2" />
            {saving ? 'Saving…' : isEdit ? 'Update User' : 'Create User'}
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => navigate('/admin/users')}>Cancel</CButton>
        </div>
      </CForm>
    </>
  );
}
